/**
 * Generic retry-with-exponential-backoff wrapper (Phase 1 — Upload Pipeline Fixes).
 *
 * Used to wrap network calls (uploads, form saves) so a dropped request on a
 * flaky mobile connection is retried automatically instead of failing
 * outright on the first attempt.
 *
 * NOTE ON IDEMPOTENCY: retrying a write (upload/save) can, in principle,
 * cause a duplicate if the first attempt actually succeeded server-side but
 * the response was lost. Callers that mutate data should pass a stable
 * `idempotencyKey` through to the server-side request itself (separately
 * from this utility) so retries are safe to resend.
 */

const DEFAULT_RETRY_OPTIONS = {
  retries: 2,
  baseDelayMs: 800,
  maxDelayMs: 8000,
  isRetryable: (error) => {
    // Network failures (fetch throws a TypeError) and 5xx/408/429 responses
    // are worth retrying. 4xx validation/auth errors are not.
    if (!error) return false
    if (error.name === "TypeError") return true
    const status = error.status || error?.response?.status
    if (!status) return true
    return status === 408 || status === 429 || status >= 500
  },
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Runs `fn` and retries on failure with exponential backoff + jitter.
 * `fn` is called with the current attempt number (0-indexed).
 */
export async function retryWithBackoff(fn, options = {}) {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options }
  let lastError

  for (let attempt = 0; attempt <= opts.retries; attempt++) {
    try {
      return await fn(attempt)
    } catch (error) {
      lastError = error
      const isLastAttempt = attempt === opts.retries
      if (isLastAttempt || !opts.isRetryable(error)) {
        throw error
      }
      const exponential = Math.min(opts.baseDelayMs * 2 ** attempt, opts.maxDelayMs)
      const jitter = Math.random() * exponential * 0.3
      console.log(`[v0] Retrying after failure (attempt ${attempt + 1}/${opts.retries}):`, error?.message)
      await delay(exponential + jitter)
    }
  }

  throw lastError
}
